import React from 'react';
import { VIEW_WIDTH, VIEW_HEIGHT, fieldToScreen, losScreenY, losBandScreenRange } from './fieldCoordinates';

// Playbook field renderer (034) — PURE presentation. Knows nothing about
// editing, markers, or which unit_type is being authored beyond the
// field_config it's given. This is deliberately the one piece a future
// read-only Film Study viewer reuses verbatim (see project-wide
// renderer/editor separation requirement) — no drag/click handlers live
// here at all; those belong to the editor that composes this component
// with <PlaybookMarker> children.
//
// Renders: green field, yard lines every 5yd across the visible window,
// a highlighted Line of Scrimmage, and white sideline borders. Accepts
// children so a caller can layer markers (or, later, routes/Vision/etc.)
// in the exact same coordinate space via fieldCoordinates.js's exported
// mapping functions.
export default function FootballField({ fieldConfig, onFieldPointerDown, children }) {
  const beforeLos = fieldConfig?.before_los_yd ?? 15;
  const afterLos = fieldConfig?.after_los_yd ?? 15;
  const losY = losScreenY(fieldConfig);
  const { nearY: losBandNearY, farY: losBandFarY } = losBandScreenRange(fieldConfig);

  const yardLines = [];
  for (let yd = -beforeLos; yd <= afterLos; yd += 5) {
    if (yd === 0) continue; // LOS drawn separately, more prominent
    const { py } = fieldToScreen(fieldConfig, 0, yd);
    yardLines.push(
      <line key={yd} x1={0} y1={py} x2={VIEW_WIDTH} y2={py} stroke="#1f6b38" strokeWidth={1} />
    );
  }

  return (
    <svg
      viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
      width="100%" height="auto"
      style={{ display: 'block', touchAction: 'none', backgroundColor: '#0d3a1c', borderRadius: 8 }}
      onPointerDown={onFieldPointerDown}
    >
      {yardLines}
      {/* LOS neutral-zone band — a subtle rendering-only widening of the
          line itself, so the offensive line reads as "just behind it"
          instead of every marker looking bisected by a single pixel-thin
          rule. y = 0 stays the exact, canonical LOS coordinate — drawn
          as the precise dashed line at its center, on top of the band. */}
      <rect x={0} y={losBandFarY} width={VIEW_WIDTH} height={Math.max(0, losBandNearY - losBandFarY)} fill="#f5a623" fillOpacity={0.16} />
      <line x1={0} y1={losY} x2={VIEW_WIDTH} y2={losY} stroke="#f5a623" strokeWidth={2} strokeDasharray="6 4" />
      {/* Sidelines */}
      <line x1={1} y1={0} x2={1} y2={VIEW_HEIGHT} stroke="#e8f0f8" strokeWidth={2} />
      <line x1={VIEW_WIDTH - 1} y1={0} x2={VIEW_WIDTH - 1} y2={VIEW_HEIGHT} stroke="#e8f0f8" strokeWidth={2} />
      {children}
    </svg>
  );
}
